import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * MAISON ARPENT — Sélecteur de format à gabarit comparé.
 *
 * Deux responsabilités, pas une de plus :
 *  1. un clic sur un format déclenche l'entrée correspondante du `variant-picker`
 *     natif — la sélection de variante n'est jamais recodée ici ;
 *  2. le bloc SUIT le changement de variante venu d'ailleurs (nuancier de coloris,
 *     Arpentage) via `StandardEvents.productSelect`, et remet à jour `aria-pressed`
 *     et les prix.
 *
 * En mode REPLIÉ (`<details class="__volet">`), il referme le volet après un choix
 * et recopie dans le résumé le contenu du format retenu. Rien n'est redessiné : on
 * clone les nœuds du bouton, donc le résumé ne peut pas diverger de la liste.
 *
 * Les dimensions et les prix ne sont jamais lus dans la charge de l'événement :
 * ils viennent du tableau écrit en Liquid dans `ref="donnees"`. Les prix y sont
 * déjà formatés par Shopify — rien n'est reconstruit en JS.
 *
 * @typedef {object} Variante
 * @property {number} id
 * @property {string} taille
 * @property {string} coloris
 * @property {string[]} options
 * @property {number} largeur
 * @property {number} longueur
 * @property {string} forme
 * @property {number} prix
 * @property {string} prix_texte
 * @property {string} prix_m2_texte
 * @property {boolean} disponible
 *
 * @typedef {object} Donnees
 * @property {string} option
 * @property {number} idx_taille
 * @property {Variante[]} variantes
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} donnees
 *
 * @extends {Component<Refs>}
 */
class SelecteurFormat extends Component {
  requiredRefs = ['donnees'];

  /** @type {Donnees | null} */
  #donnees = null;

  /** @type {Element | null} Le conteneur qui émet `productSelect`. */
  #contexte = null;

  connectedCallback() {
    super.connectedCallback();
    this.#lireDonnees();
    this.#contexte = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#contexte?.addEventListener(StandardEvents.productSelect, this.#surVariante);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#contexte?.removeEventListener(StandardEvents.productSelect, this.#surVariante);
    this.#contexte = null;
  }

  /** Le rendu serveur a remplacé le bloc : les prix ont pu changer de coloris. */
  updatedCallback() {
    super.updatedCallback();
    this.#lireDonnees();
  }

  /**
   * Clic sur un format. Lié en déclaratif par `on:click="/choisir"`.
   * @param {Event} event
   */
  choisir(event) {
    const cible = event.target;
    const bouton = cible instanceof Element ? cible.closest('button[data-taille]') : null;
    const taille = bouton?.dataset.taille;
    if (!taille) return;

    // État optimiste : le bouton répond tout de suite, le serveur confirmera.
    this.#peindre(taille);
    this.#deleguerAuPicker(taille);
    this.#replier();
  }

  /** Referme le volet, s'il y en a un. */
  #replier() {
    this.querySelector('[data-sf="volet"]')?.removeAttribute('open');
  }

  /** Recopie dans le résumé le contenu du format retenu. */
  #resumer() {
    const resume = this.querySelector('[data-sf="resume"]');
    const choisi = this.querySelector('button[data-taille][aria-pressed="true"]');
    if (!resume || !choisi) return;

    resume.replaceChildren(...[...choisi.cloneNode(true).childNodes]);
  }

  /** Lit le tableau des variantes écrit en Liquid. */
  #lireDonnees() {
    const contenu = this.refs.donnees?.textContent;
    if (!contenu) return;

    try {
      const brut = JSON.parse(contenu);
      if (Array.isArray(brut?.variantes)) this.#donnees = brut;
    } catch (erreur) {
      console.warn('[selecteur-format] tableau des variantes illisible', erreur);
    }
  }

  /**
   * Déclenche l'entrée du `variant-picker` natif qui porte cette valeur d'option.
   * C'est exactement ce que ferait un clic de l'utilisateur sur le sélecteur natif.
   * @param {string} taille
   * @returns {boolean} vrai si une entrée a été trouvée
   */
  #deleguerAuPicker(taille) {
    const nom = this.#donnees?.option;
    if (!nom) return false;

    const picker = (this.#contexte ?? document).querySelector('variant-picker');
    if (!picker) return false;

    // Style « boutons » et « nuanciers » : des boutons radio.
    for (const entree of picker.querySelectorAll('input[type="radio"]')) {
      if (!(entree instanceof HTMLInputElement)) continue;
      if (entree.dataset.optionName !== nom || entree.value !== taille) continue;
      if (entree.checked) return true;

      entree.checked = true;
      entree.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    // Style « listes déroulantes ».
    for (const liste of picker.querySelectorAll('select')) {
      if (!(liste instanceof HTMLSelectElement)) continue;

      for (const choix of liste.options) {
        if (choix.dataset.optionName !== nom || choix.value !== taille) continue;
        if (liste.value === choix.value) return true;

        liste.value = choix.value;
        liste.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    return false;
  }

  /**
   * Le changement de variante est venu d'ailleurs : on suit.
   * @param {Event & { promise: Promise<{ detail: any }> }} event
   */
  #surVariante = (event) => {
    event.promise
      .then(({ detail }) => {
        const id = detail?.resource?.id;
        if (!id || !this.#donnees) return;

        const courante = this.#donnees.variantes.find((v) => String(v.id) === String(id));
        if (!courante) return;

        this.#peindre(courante.taille, courante.options);
        this.#replier();
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[selecteur-format]', erreur);
      });
  };

  /**
   * Remet à jour l'état pressé et, si on connaît les autres options retenues,
   * les prix de chaque format pour ce coloris.
   * @param {string} taille
   * @param {string[]} [options] Les options de la variante retenue.
   */
  #peindre(taille, options) {
    const donnees = this.#donnees;

    for (const bouton of this.querySelectorAll('button[data-taille]')) {
      if (!(bouton instanceof HTMLElement)) continue;
      const valeur = bouton.dataset.taille;
      bouton.setAttribute('aria-pressed', String(valeur === taille));

      if (!donnees || !options || !valeur) continue;

      const variante = donnees.variantes.find(
        (v) =>
          v.taille === valeur &&
          Array.isArray(v.options) &&
          v.options.every((o, i) => i === donnees.idx_taille || o === options[i])
      );
      if (!variante) continue;

      const prix = bouton.querySelector('[data-prix]');
      if (prix && variante.prix_texte) prix.textContent = variante.prix_texte;

      const prixM2 = bouton.querySelector('[data-prix-m2]');
      if (prixM2 instanceof HTMLElement) {
        prixM2.textContent = variante.prix_m2_texte || '';
        prixM2.hidden = !variante.prix_m2_texte;
      }
    }

    // Le résumé se recopie EN DERNIER : il cloue l'état final de la liste.
    this.#resumer();
  }
}

if (!customElements.get('selecteur-format')) {
  customElements.define('selecteur-format', SelecteurFormat);
}
